import { applyCarrierEvent, carriersFromStart, countStreamedRate, insertSorted, readSse, type StreamCarrier } from "@flexfreight/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, apiEventStream, StreamUnavailableError } from "../../api/client";
import type { ConsoleQuoteOption, ConsoleQuoteResult } from "./QuotingCenter";

/**
 * Quoting center, progressive: POST /v1/console/quotes/stream sends each priced option (`rate`) the moment its
 * carrier answers, a `carrier` event as each carrier settles, then `done` with exactly the JSON route's body. When
 * the stream can't be opened, the JSON route runs instead; either way the result lands through `onSuccess`.
 */

/** Rates on screen while the stream runs (cheapest first) and each carrier's progress. */
export interface LiveRates {
  options: ConsoleQuoteOption[];
  carriers: StreamCarrier[];
}

export async function streamConsoleQuote(body: unknown, onLive: (update: (l: LiveRates) => LiveRates) => void, signal: AbortSignal): Promise<ConsoleQuoteResult> {
  const res = await apiEventStream("/v1/console/quotes/stream", body, { signal });
  let done: ConsoleQuoteResult | undefined;
  let failed: ApiError | undefined;
  try {
    await readSse(res.body!, (e) => {
      let d: unknown;
      try {
        d = JSON.parse(e.data);
      } catch {
        return;
      }
      if (!d || typeof d !== "object") return;
      if (e.event === "start") {
        const carriers = ((d as { carriers?: { carrier: string; name: string }[] }).carriers ?? []);
        onLive(() => ({ options: [], carriers: carriersFromStart(carriers) }));
      } else if (e.event === "rate") {
        const o = d as ConsoleQuoteOption;
        onLive((l) => ({ options: insertSorted(l.options, o, (x) => x.price.cents), carriers: countStreamedRate(l.carriers, o.carrier) }));
      } else if (e.event === "carrier") {
        onLive((l) => ({ ...l, carriers: applyCarrierEvent(l.carriers, d as Parameters<typeof applyCarrierEvent>[1]) }));
      } else if (e.event === "done") {
        done = d as ConsoleQuoteResult;
      } else if (e.event === "error") {
        const err = d as { code?: string; message?: string };
        failed = new ApiError(0, err.code ?? "stream_error", err.message ?? "Couldn't finish the quote");
      }
    });
  } catch (e) {
    if (signal.aborted) throw e;
    if (!done) throw new ApiError(0, "stream_broken", "The connection dropped while carriers were answering. Try again.");
  }
  if (failed) throw failed;
  if (!done) throw new ApiError(0, "stream_ended", "The rate stream ended before the quote was saved. Try again.");
  return done;
}

/** A react-query-mutation-shaped hook: `mutate(body)`, `isPending`, plus `live` while the stream runs. */
export function useConsoleQuoteStream(handlers: { onSuccess: (r: ConsoleQuoteResult) => void; onError: (e: unknown) => void }) {
  const [isPending, setPending] = useState(false);
  const [live, setLive] = useState<LiveRates | null>(null);
  const run = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => () => abort.current?.abort(), []);

  const mutate = useCallback((body: unknown) => {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    const id = ++run.current;
    const mine = () => run.current === id;
    setPending(true);
    setLive(null);
    const onLive = (update: (l: LiveRates) => LiveRates) => mine() && setLive((l) => update(l ?? { options: [], carriers: [] }));
    streamConsoleQuote(body, onLive, ac.signal)
      .catch((e: unknown) => {
        if (e instanceof StreamUnavailableError) {
          if (mine()) setLive(null);
          return api<ConsoleQuoteResult>("/v1/console/quotes", { method: "POST", body, signal: ac.signal });
        }
        throw e;
      })
      .then(
        (r) => {
          if (!mine()) return;
          setPending(false);
          setLive(null);
          latest.current.onSuccess(r);
        },
        (e: unknown) => {
          if (!mine() || ac.signal.aborted) return;
          setPending(false);
          setLive(null);
          latest.current.onError(e);
        },
      );
  }, []);

  return { mutate, isPending, live };
}
